import { onValue, set } from 'firebase/database';
import { tenantRef, logPosEvent } from './posUtil';
import { userFacingError } from './imageCompressionUtil';
import {
  POS_CUSTOM_SORT_NODE,
  POS_CUSTOM_SORT_LISTS,
  POS_AUDIT_ACTIONS,
} from '../constants/posConst';

// ---- Custom (hand-arranged) product order ---------------------------------
//
// Stored as ONE ARRAY OF PRODUCT IDS per list, under
// posTenants/<clientId>/customSort/<listKey>. An array rather than a
// `sortOrder` number on each product because an arrangement is a property of
// the LIST, not of the product: saving it is one write and it cannot
// half-apply. The Products screen and Inventory share one list, so an order
// saved on either applies to both.

/** Human names for the audit trail. */
const LIST_LABELS = {
  [POS_CUSTOM_SORT_LISTS.SHARED]: 'Products and Inventory',
};

/**
 * Throws unless `listKey` is one of POS_CUSTOM_SORT_LISTS.
 *
 * @param {string} listKey
 * @returns {void}
 * @throws {Error} on an unknown list — a typo must not create a stray node
 */
const assertListKey = (listKey) => {
  if (!Object.values(POS_CUSTOM_SORT_LISTS).includes(listKey)) {
    throw new Error(`posCustomSortUtil: unknown custom sort list "${listKey}".`);
  }
};

/**
 * Live saved order for one list.
 *
 * @param {string} clientId
 * @param {string} listKey - a POS_CUSTOM_SORT_LISTS value
 * @param {(ids: string[]) => void} callback - [] when nothing is saved yet
 * @returns {() => void} unsubscribe
 */
export const subscribePosCustomSort = (clientId, listKey, callback) => {
  assertListKey(listKey);
  return onValue(
    tenantRef(clientId, `${POS_CUSTOM_SORT_NODE}/${listKey}`),
    (snapshot) => {
      const value = snapshot.val();
      callback(Array.isArray(value) ? value.filter((id) => typeof id === 'string' && id) : []);
    },
    (error) => {
      // A failed read leaves the grid in name order rather than blank.
      console.error('POS custom sort listener failed:', error);
      callback([]);
    },
  );
};

/**
 * The full arrangement for a product list: the saved ids that still exist, in
 * their saved order, followed by every product not yet arranged, A-Z.
 *
 * This is what a drag edits, so a product registered after the last save
 * still has a place to be dragged from.
 *
 * @param {string[]} savedIds
 * @param {object[]} products - needs `id` and `name`
 * @returns {string[]} product ids
 */
export const buildPosCustomOrder = (savedIds, products) => {
  const existing = new Set((products || []).map((product) => product.id));
  const seen = new Set();
  const ordered = [];
  (savedIds || []).forEach((id) => {
    if (existing.has(id) && !seen.has(id)) {
      seen.add(id);
      ordered.push(id);
    }
  });
  const rest = (products || [])
    .filter((product) => !seen.has(product.id))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base', numeric: true }))
    .map((product) => product.id);
  return [...ordered, ...rest];
};

/**
 * Moves one id to where another currently sits.
 *
 * Works on the FULL arrangement even when the screen shows a filtered subset:
 * the visible list is always a subsequence of this one, so moving within it
 * keeps every hidden product exactly where it was.
 *
 * @param {string[]} order - the full arrangement
 * @param {string} fromId - the product being dragged
 * @param {string} toId - the product it was dragged onto
 * @returns {string[]} a new array; `order` itself is unchanged
 */
export const movePosCustomOrder = (order, fromId, toId) => {
  const from = order.indexOf(fromId);
  const to = order.indexOf(toId);
  if (from < 0 || to < 0 || from === to) return order;
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, fromId);
  return next;
};

/**
 * Saves one list's arrangement and records it on the audit trail.
 *
 * @param {string} clientId
 * @param {string} listKey - a POS_CUSTOM_SORT_LISTS value
 * @param {string[]} ids - product ids in order
 * @param {string} [actor] - the staff address to record
 * @returns {Promise<string[]>} what was saved
 * @throws {Error} user-facing, on an empty arrangement
 */
export const savePosCustomSort = async (clientId, listKey, ids, actor = '') => {
  assertListKey(listKey);
  const clean = [...new Set((ids || []).filter((id) => typeof id === 'string' && id))];
  if (clean.length === 0) throw userFacingError('There are no products to arrange yet.');

  await set(tenantRef(clientId, `${POS_CUSTOM_SORT_NODE}/${listKey}`), clean);

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.CUSTOM_SORT_SAVED,
    entityId: listKey,
    summary: { list: LIST_LABELS[listKey] || listKey, products: clean.length },
  });
  return clean;
};
